import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#fbDisableCartScrollLock();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  // FB FIX (arka plan kaymasi): Sepet drawer'i acikken sayfanin arkada kaymasini olay seviyesinde engelle.
  // touch-action:none tek basina yetmiyor: PC'de mouse tekerlegini HIC etkilemez, mobilde de drawer icindeki
  // scroll sinira gelince bazi tarayicilar scroll'u arka plana zincirler (scroll chaining).
  // Bu kilit: drawer ICINDEKI kaydirilabilir alanda scroll'a izin verir; alan sinirindaysa veya hedef drawer
  // disindaysa (backdrop dahil) event'i engeller -> arka plan ASLA kaymaz. Scroll pozisyonuna, overflow'a,
  // sticky header'a dokunmaz (isinlanma fix'i korunur). SADECE cart drawer'a uygulanir, diger dialoglar aynen kalir.
  /** @type {AbortController | null} */
  #fbCartScrollLock = null;

  #fbEnableCartScrollLock() {
    const { dialog } = this.refs;
    if (!dialog) return;

    this.#fbCartScrollLock?.abort();
    this.#fbCartScrollLock = new AbortController();
    const { signal } = this.#fbCartScrollLock;

    /** @param {EventTarget | null} target */
    const findScrollable = (target) => {
      let el = target instanceof Element ? target : null;
      while (el && dialog.contains(el)) {
        if (el.scrollHeight > el.clientHeight + 1) {
          const overflowY = getComputedStyle(el).overflowY;
          if (overflowY === 'auto' || overflowY === 'scroll') return el;
        }
        el = el.parentElement;
      }
      return null;
    };

    /**
     * @param {EventTarget | null} target
     * @param {boolean} goingDown
     */
    const shouldBlock = (target, goingDown) => {
      const scrollable = findScrollable(target);
      if (!scrollable) return true;
      const atTop = scrollable.scrollTop <= 0;
      const atBottom = scrollable.scrollTop + scrollable.clientHeight >= scrollable.scrollHeight - 1;
      return (goingDown && atBottom) || (!goingDown && atTop);
    };

    document.addEventListener(
      'wheel',
      (event) => {
        if (!dialog.open) return;
        if (shouldBlock(event.target, event.deltaY > 0)) event.preventDefault();
      },
      { passive: false, capture: true, signal }
    );

    let lastTouchY = 0;
    let fbLastTouchX = 0;
    document.addEventListener(
      'touchstart',
      (event) => {
        lastTouchY = event.touches[0]?.clientY ?? 0;
        fbLastTouchX = event.touches[0]?.clientX ?? 0;
      },
      { passive: true, capture: true, signal }
    );

    document.addEventListener(
      'touchmove',
      (event) => {
        if (!dialog.open) return;
        const currentY = event.touches[0]?.clientY ?? lastTouchY;
        const currentX = event.touches[0]?.clientX ?? fbLastTouchX;
        // FB KOF SCROLL FIX: yatay agirlikli jestlere karisma -> drawer icindeki yatay
        // scroller'larin (KOF listesi) native momentum'u olmesin. Dikey kilit aynen kalir;
        // arka plan zaten html{touch-action:none} ile kilitli, yatayda sizinti olmaz.
        const fbHorizontal = Math.abs(currentX - fbLastTouchX) > Math.abs(currentY - lastTouchY);
        const goingDown = currentY < lastTouchY;
        if (!fbHorizontal && shouldBlock(event.target, goingDown)) {
          event.preventDefault();
        }
        lastTouchY = currentY;
        fbLastTouchX = currentX;
      },
      { passive: false, capture: true, signal }
    );
  }

  #fbDisableCartScrollLock() {
    this.#fbCartScrollLock?.abort();
    this.#fbCartScrollLock = null;
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // FB KOK COZUM (isinlanma + header): scroll'u position:fixed (top:-Y) DA DEGIL, overflow:hidden DA DEGIL,
    // touch-action:none ile kilitle. Sebep:
    //  - position:fixed scroll'u KAYDIRIR -> kapanista scrollTo geri-yukleme -> ilk dokunusta TEPEYE isinlanma.
    //  - overflow:hidden scroll'u kaydirmaz AMA sticky header'i kirar -> logo/ust alan gizlenir.
    //  - touch-action:none mobilde arka plan dokunmatik-scroll'unu durdurur, scroll'u KAYDIRMAZ (teleport yok)
    //    ve overflow/sticky'ye dokunmaz (header korunur). Drawer showModal ile top-layer'da -> kendi scroll'u etkilenmez.
    const openDialog = () => {
      if (dialog.open) return;

      document.documentElement.style.touchAction = 'none';
      document.documentElement.style.overscrollBehavior = 'none';

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      // FB FIX (arka plan kaymasi): sadece sepet drawer'inda olay-seviyesi scroll kilidini ac.
      if (this.tagName === 'CART-DRAWER-COMPONENT') {
        this.#fbEnableCartScrollLock();
      }

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    };

    // FB SEPET ANINDA ACILIS: cart drawer pointerdown turunda showModal() olur; sayfa yuklenirken
    // requestAnimationFrame kuyrugunun 100-150ms sarkmasi beklenmez. Diger dialoglarin davranisi aynen korunur.
    if (this.tagName === 'CART-DRAWER-COMPONENT') {
      openDialog();
    } else {
      requestAnimationFrame(openDialog);
    }
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    const fbIsCart = this.tagName === 'CART-DRAWER-COMPONENT';

    // Yumusak slide-out animasyonu (scroll kilidi acik kalir; dialog position:fixed oldugu icin puruzsuz oynar)
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    // FB: kapanis animasyonunu BEKLE ama guvenli ust sinir kadar (asla kilitlenme).
    await Promise.race([
      onAnimationEnd(dialog, undefined, { subtree: false }),
      new Promise((resolve) => setTimeout(resolve, fbIsCart ? 320 : 600)),
    ]);

    dialog.close();
    dialog.classList.remove('dialog-closing');

    // FB FIX (arka plan kaymasi): olay-seviyesi scroll kilidini kaldir.
    this.#fbDisableCartScrollLock();

    // FB KOK COZUM (isinlanma): scroll showDialog'da touch-action:none ile kilitlendi (KAYDIRILMADI).
    // Kapanista SADECE kilidi ac -> geri-yuklenecek / scrollTo edilecek scroll YOK -> "kapaninca yukari isinlanma" IMKANSIZ.
    // (Onceki surumlerden overflow / position:fixed kalintisi varsa diye onlar da temizlenir.)
    document.documentElement.style.touchAction = '';
    document.documentElement.style.overscrollBehavior = '';
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);